/**
 * Leer un token del tema y escribirlo como CSS: la variable de un color por su
 * id, y las reglas de un estilo de texto o de botón con sus tamaños.
 *
 * Lo usa el aspecto de la página de una oferta (`offerPageStyle`). Se guarda el
 * ID de un token del editor y no su valor de hoy; el CSS se escribe contra el
 * tema cada vez, así que cambiar la marca en el editor cambia también la página.
 * Un token borrado deja de pintarse en vez de romper nada.
 */
import type { GlobalButtonComponent, GlobalColorComponent, GlobalTypographyComponent, PageTheme } from './types'
import { componentStates, hasInteractionStates, stateTransitionCss } from './interactionStates'

/** El tema con sus tokens, si los trae: una `PageTheme` antigua no los tiene. */
export type ThemeWithTokens = PageTheme & {
  colorComponents?: GlobalColorComponent[] | undefined
  typographyComponents?: GlobalTypographyComponent[] | undefined
  buttonComponents?: GlobalButtonComponent[] | undefined
}

const CSS_VAR = /^--[A-Za-z0-9_-]{1,80}$/
const PROPERTY = /^[A-Za-z]{1,60}$/

/**
 * Un valor que pueda ir dentro de una declaración y no salir de ella: sin `;`
 * no abre otra, sin llaves no cierra la regla y sin `<` no cierra el `<style>`.
 * La misma limpieza que `brandTokens` en el worker.
 */
function cssValue(value: unknown): string {
  return typeof value === 'string' ? value.replace(/[<>{};\\]/g, '').trim() : ''
}

/**
 * Los estilos de un token como declaraciones CSS, sin márgenes: el aire entre
 * bloques es de la página, no del token — un H2 de portada trae márgenes
 * pensados para una sección, no para una ficha.
 */
function tokenDeclarations(styles: object | undefined): string {
  const out: string[] = []
  for (const [key, value] of Object.entries(styles ?? {})) {
    if (!PROPERTY.test(key) || key.startsWith('margin')) continue
    const clean = cssValue(value)
    if (!clean) continue
    out.push(`${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}:${clean}`)
  }
  return out.join(';')
}

/**
 * La variable de un color del tema, por su id, o nada si ya no existe.
 *
 * Nada y no el de fábrica: vacío significa «como hoy», y un color borrado tiene
 * que volver a eso, no a otro color que nadie eligió.
 */
export function themeColorVar(theme: ThemeWithTokens, id: string): string | null {
  if (!id) return null
  const token = theme.colorComponents?.find((item) => item.id === id)
  if (token?.cssVar && CSS_VAR.test(token.cssVar)) return token.cssVar
  return id in theme.colors ? `--wb-${id}` : null
}

/** Las reglas de un token para un selector, con tableta y móvil. */
export function tokenRules(selector: string, token: { styles?: object | undefined; breakpoints?: { tablet?: object | undefined; mobile?: object | undefined } | undefined } | undefined, extra = ''): string {
  if (!token) return ''
  const join = (css: string) => [css, extra].filter(Boolean).join(';')
  const desktop = tokenDeclarations(token.styles)
  const tablet = tokenDeclarations({ ...token.styles, ...token.breakpoints?.tablet })
  const mobile = tokenDeclarations({ ...token.styles, ...token.breakpoints?.mobile })
  const parts: string[] = []
  if (desktop || extra) parts.push(`${selector}{${join(desktop)}}`)
  /* Los mismos cortes que el editor, y cada tamaño sólo si cambia respecto al
     anterior: `max-width:1024px` también se cumple en un móvil. */
  let previous = desktop
  if (tablet && tablet !== previous) {
    parts.push(`@media (max-width: 1024px){${selector}{${tablet}}}`)
    previous = tablet
  }
  if (mobile && mobile !== previous) parts.push(`@media (max-width: 640px){${selector}{${mobile}}}`)
  return parts.join('\n')
}

/**
 * Hover, presionado y enfocado de un botón global, con su transición: lo mismo
 * que hereda un botón del sitio (ver `interactionStates`), para las páginas que
 * arma el worker —ofertas y reservas—. Sin esto el botón de comprar era el único
 * del sitio que no reaccionaba al ratón.
 */
export function tokenStateRules(selector: string, token: GlobalButtonComponent | undefined): string {
  if (!token) return ''
  const states = componentStates(token, 'button')
  if (!hasInteractionStates(states)) return ''
  const parts = [`${selector}{transition:${stateTransitionCss(token.transition)}}`]
  const pseudo = { hover: ':hover', pressed: ':active', focus: ':focus-visible' } as const
  for (const state of ['hover', 'pressed', 'focus'] as const) {
    const css = tokenDeclarations(states[state])
    if (css) parts.push(`${selector}${pseudo[state]}{${css}}`)
  }
  return parts.join('\n')
}

/** El CTA del contenido publicado usa el botón principal de la marca. */
export function themePrimaryButtonCss(theme: {
  roles?: { buttonPrimary?: object | undefined } | undefined
  buttonComponents?: GlobalButtonComponent[] | undefined
} | undefined, selector: string): string {
  const token = theme?.buttonComponents?.find((item) => item.id === 'button-primary')
  return [
    tokenRules(selector, token ?? { styles: theme?.roles?.buttonPrimary }),
    tokenStateRules(selector, token),
  ].filter(Boolean).join('\n')
}

/**
 * Las piezas del blog (filtros, temas, autores, recientes) con los estilos
 * globales del sitio: la letra del párrafo en campos y listas, la del título 4
 * en sus títulos y el botón principal en «Buscar». Las reglas van con `.vbw`
 * delante para ganar a la hoja base (`BLOG_WIDGET_CSS`). Sin tema, nada.
 */
/** Los estilos del botón principal: los de su componente global o, sin él, los del papel. */
function primaryButtonStyles(theme: { roles?: Record<string, object | undefined> | undefined; buttonComponents?: GlobalButtonComponent[] | undefined }): Record<string, unknown> | undefined {
  return (theme.buttonComponents?.find((item) => item.id === 'button-primary')?.styles ?? theme.roles?.buttonPrimary) as Record<string, unknown> | undefined
}

export function blogWidgetTokenCss(theme: {
  roles?: Record<string, object | undefined> | undefined
  typographyComponents?: Array<{ id: string; styles?: object | undefined }> | undefined
  buttonComponents?: GlobalButtonComponent[] | undefined
} | undefined): string {
  if (!theme) return ''
  const typography = (id: string, role: string) => (
    theme.typographyComponents?.find((item) => item.id === id)?.styles ?? theme.roles?.[role]
  ) as Record<string, unknown> | undefined
  const pick = (styles: Record<string, unknown> | undefined, keys: string[]) => ({
    styles: Object.fromEntries(keys.filter((key) => styles?.[key] !== undefined && styles[key] !== '').map((key) => [key, styles![key]])),
  })
  return [
    tokenRules('.vbw,.vbw .vbw-field,.vbw .vbw-chips a,.vbw .vbw-name', pick(typography('p', 'paragraph'), ['fontFamily', 'letterSpacing'])),
    tokenRules('.vbw .vbw-title', pick(typography('h4', 'heading4') ?? typography('h3', 'heading3'), ['fontFamily', 'fontWeight', 'letterSpacing', 'textTransform'])),
    themePrimaryButtonCss(theme as Parameters<typeof themePrimaryButtonCss>[0], '.vbw .vbw-go'),
    /* Los campos (buscar, temas, autores, orden, el correo del boletín) con las
       esquinas del botón principal, para que vayan a juego con «Buscar». */
    tokenRules('.vbw .vbw-field', pick(primaryButtonStyles(theme), ['borderRadius', 'borderTopLeftRadius', 'borderTopRightRadius', 'borderBottomRightRadius', 'borderBottomLeftRadius'])),
  ].filter(Boolean).join('\n')
}

/**
 * Los estilos de texto que el dueño eligió para UNA pieza del blog (su
 * título, su texto y su fecha o contador), con el selector de esa pieza
 * (`scope`). Después, los tamaños propios de la pieza, que mandan sobre el
 * del estilo. Vacío si no eligió nada.
 */
export function blogWidgetStyleCss(theme: {
  typographyComponents?: Array<{ id: string; styles?: object | undefined; breakpoints?: { tablet?: object | undefined; mobile?: object | undefined } | undefined }> | undefined
} | undefined, scope: string, settings: { titleStyle?: string | undefined; textStyle?: string | undefined; metaStyle?: string | undefined; titleSize?: number | undefined; textSize?: number | undefined }): string {
  const token = (id: string | undefined) => (id ? theme?.typographyComponents?.find((item) => item.id === id) : undefined)
  const parts = [
    tokenRules(`${scope} .vbw-title`, token(settings.titleStyle)),
    tokenRules(`${scope} :is(.vbw-name,.vbw-chips a,.vbw-cloud a,.vbw-field,.vbw-go,.vbw-newsletter p,.vbw-profile-text p,.vbw-toc a,.vbw-crumbs ol,.vbw-share a,.vbw-share button)`, token(settings.textStyle)),
    tokenRules(`${scope} :is(.vbw-date,.vbw-count,.vbw-adjacent small)`, token(settings.metaStyle)),
  ]
  if (settings.titleSize && settings.titleStyle) parts.push(`${scope} .vbw-title{font-size:var(--vbw-title-size)}`)
  if (settings.textSize && settings.textStyle) parts.push(`${scope} :is(.vbw-name,.vbw-chips a,.vbw-field,.vbw-go){font-size:var(--vbw-text-size)}`)
  return parts.filter(Boolean).join('\n')
}

/**
 * El paginador «‹ 1 2 3 ›» de una página que arma el worker (la lista del
 * blog) con los estilos globales: la letra del párrafo en los números y, en la
 * página actual, el fondo y la tinta del botón principal. Sólo color y letra:
 * su forma redonda es la del paginador, no la del botón.
 */
export function pagerTokenCss(theme: {
  roles?: Record<string, object | undefined> | undefined
  typographyComponents?: Array<{ id: string; styles?: object | undefined }> | undefined
  buttonComponents?: GlobalButtonComponent[] | undefined
} | undefined): string {
  if (!theme) return ''
  const pick = (styles: object | undefined, keys: string[]) => ({
    styles: Object.fromEntries(keys.filter((key) => (styles as Record<string, unknown> | undefined)?.[key] !== undefined && (styles as Record<string, unknown>)[key] !== '').map((key) => [key, (styles as Record<string, unknown>)[key]])),
  })
  const paragraph = theme.typographyComponents?.find((item) => item.id === 'p')?.styles ?? theme.roles?.paragraph
  const button = theme.buttonComponents?.find((item) => item.id === 'button-primary')?.styles ?? theme.roles?.buttonPrimary
  return [
    tokenRules('.pager a,.pager span', pick(paragraph, ['fontFamily', 'letterSpacing'])),
    tokenRules('.pager .is-current', pick(button, ['background', 'backgroundColor', 'color'])),
  ].filter(Boolean).join('\n')
}
